import './postHistoryStyles.css';
import postHistoryView from './postHistoryComponent.html';
import { ensure } from 'components/sharpr/ensure/ensure.js';
import { AuthTypes } from '../../auth/AuthTypes';

export default function postHistoryComponent() {
	return {
		restrict: 'E',
		scope: {},
		bindToController: {
			post: '=',
			comments: '<?',
			likes: '<?',
			limit: '<',
			showInput: '=?',
			refreshComments: '=',
		},
		controllerAs: 'vm',
		controller: postHistoryComponentCtrl,
		template: postHistoryView,
	};
}

class postHistoryComponentCtrl {
	constructor($scope, Cfg, Identity, Api, modal, $timeout, $q) {
		'ngInject';
		// save injections
		this.$scope = $scope;
		this.Cfg = Cfg;
		this.Identity = Identity;
		this.Api = Api;
		this.modal = modal;
		this.$timeout = $timeout;
		this.$q = $q;
	}
	$onInit() {
		this.hideLikesAndComments = this.Cfg.read('Features.hide_likes_and_comments') !== '0';
		if (this.hideLikesAndComments) {
			return;
		}
		//check for private comments feature
		this.privateComments = this.Cfg.read('Features.private_comments') === '1';

		// setup user
		this.currentUser = this.Identity.user();
		this.canEditComments = this.Identity.can('edit_comments');
		this.canDeleteComments = this.Identity.can('delete_comments');
		this.canMentionUsers = this.Identity.can('mention_users');
		// setup UI
		this.commentText = '';
		this.showMore = false;
		this.showMorePrivate = false;
		this.limit = this.limit || Infinity;
		this.history = [];
		this.privateCommentsHistory = {};
		this.privateCommentsLength = 0; //since private comments are a multi dimensional array we need to store the number for show more toggle
		this.newCommentIsPrivate = false;
		if (this.post && this.post.submitted_by && this.post.submitted_by.id) {
			this.userCanViewPrivateComments = this.post.submitted_by.id === this.currentUser.id; //if user is creator of post they can see private comments else they need to have submitted a private comment
		} else {
			//if no submitted by property on post then dont show private comments
			this.userCanViewPrivateComments = false;
		}

		if (this.comments || this.likes) {
			// comments and likes are already fetched, e.g. in <sh-email-post-clicks>
			this.interleaveHistory();
		} else if (this.post) {
			// we need to fetch likes and comments, e.g. in comments modal window from intel tab
			this.getComments();
		}
		this.$scope.$watch('vm.refreshComments', () => {
			if (this.refreshComments) {
				this.getComments();
				this.refreshComments = false;
			}
		});
		this.setupMentionable();
	}

	createCommentOnEnter(evt) {
		if (evt.keyCode !== 13 || evt.shiftKey) {
			return;
		} else {
			evt.preventDefault();
			this.createComment();
		}
	}

	createComment() {
		const commentText = String(this.commentText).trim();
		if (commentText === '') {
			return;
		}
		const newComment = {
			isNew: true,
			type: 'comment',
			text: commentText,
			date_created: new Date(),
			post_id: this.post.id,
			private_comment: this.newCommentIsPrivate,
			commenter: {
				id: this.currentUser.id,
				email: this.currentUser.email,
				fname: this.currentUser.fname_preferred || this.currentUser.fname,
				lname: this.currentUser.lname_preferred || this.currentUser.lname,
				avatar: this.currentUser.avatar,
			},
		};
		const addUrl = window.barHash ? `/bar/posts/comments` : `/posts/${this.post.id}/comments`;
		this.Api.create(addUrl, {
			text: commentText,
			commenter: this.currentUser.id,
			private_comment: Number(this.newCommentIsPrivate),
		}).then(commentId => {
			if (!commentId) {
				console.error('Did not receive comment id. It may not have saved. Skipping mention alerts.');
				return false;
			}

			newComment.id = commentId;
			// send mentions unless the comment is private
			if (this.mentions.length && this.canMentionUsers && !this.newCommentIsPrivate) {
				const user_ids = this.mentions.map(user => user.id);
				this.Api.post(`/posts/${this.post.id}/comments/${commentId}/mentions`, {
					user_ids,
				});
				this.mentions = [];
			}
		});
		this.history.push(newComment);
		this.commentText = '';
		this.$timeout(function () {
			newComment.isNew = false;
		}, 4000);
		if (this.privateComments) {
			this.processPrivateComments();
		}
	}

	toggleShowMore() {
		this.showMore = !this.showMore;
	}

	toggleShowMorePrivate() {
		//for private comments column
		this.showMorePrivate = !this.showMorePrivate;
	}

	interleaveHistory() {
		// get comments and likes and specify the type of each one
		const comments = ensure.array(this.comments);
		comments.forEach(c => (c.type = 'comment'));

		const likes = ensure.array(this.likes);
		likes.forEach(l => (l.type = 'like'));

		// combine comments and likes
		this.history = comments.concat(likes);
		this.history.sort((a, b) => {
			// sort oldest to newest
			return a.date_created < b.date_created ? -1 : 1;
		});
		if (this.privateComments) {
			this.processPrivateComments();
		}
	}

	editComment(comment) {
		this.editedCommentText = comment.text;
		this.editMode = true;
		this.toEdit = comment;
	}

	saveCommentOnEnter(evt) {
		if (evt.keyCode !== 13 || evt.shiftKey) {
			return;
		} else {
			evt.preventDefault();
			this.saveEdits();
		}
	}

	saveEdits() {
		// trigger the modal to close
		this.editMode = false;
		if (this.editedCommentText == this.toEdit.text) {
			// nothing to save
			return;
		}
		this.toEdit.text = this.editedCommentText;
		this.Api.patch(`/posts/${this.toEdit.post_id}/comments/${this.toEdit.id}`, {
			text: this.editedCommentText,
			date_created: this.toEdit.date_created,
			commenter: this.toEdit.commenter.id,
		});
	}

	cancelEdit() {
		this.editMode = false;
		this.editedCommentText = '';
	}

	deleteComment(comment) {
		const youSure = 'Are you sure you want to delete this comment?';
		this.modal.confirm({ text: youSure }).then(() => {
			const url = window.barHash
				? `/bar/posts/comments/${comment.id}`
				: `/posts/${comment.post_id}/comments/${comment.id}`;
			this.Api.delete(url);
			const idx = this.history.indexOf(comment);
			if (idx > -1) {
				this.history.splice(idx, 1);
			}
			if (this.privateComments) {
				this.processPrivateComments();
			}
		});
	}

	getComments() {
		if (AuthTypes.getBoardHash()) {
			return;
		}
		let headers = { Hubs: 'mine' };
		this.$q
			.all([
				this.Api.get(`/posts/${this.post.id}/comments`, null, headers),
				this.Api.get(`/posts/${this.post.id}/likes`, null, headers),
			])
			.then(([comments, likes]) => {
				this.comments = comments;
				this.likes = likes;
				this.interleaveHistory();
			});
	}

	setupMentionable() {
		this.addMention = this.addMention.bind(this);
		this.mentions = [];
		this.mentionableHandlers = [
			{
				match: /^@/,
				provider: word => {
					if (this._pendingRequest && this._pendingRequest.abort) {
						this._pendingRequest.abort();
					}
					const userSearchEndpoint = '/users/search';
					// cancel any existing requests
					const name = word
						.slice(1)
						.replace(/([A-Z])/g, ' $1')
						.trim();
					this._pendingRequest = this.Api.get(
						userSearchEndpoint,
						{
							term: name,
							role: '!observer',
							limit: 10,
						},
						{ avoidLoadingBar: true }
					);
					return this._pendingRequest.then(users => {
						const mentionedIds = this.mentions.map(u => u.id);
						users = ensure.array(users);
						users = users.filter(u => mentionedIds.indexOf(u.id) == -1);
						return users.map(user => {
							const firstName = user.fname_preferred || user.fname;
							const lastName = user.lname_preferred || user.lname;

							return {
								id: user.id,
								fname: firstName,
								lname: lastName,
								$suggest: `${firstName} ${lastName} <${user.email}>`,
								$replace: `@${firstName}${lastName}`.replace(/\s+/g, ''),
							};
						});
					});
				},
			},
		];
		if (!this.canMentionUsers) {
			this.mentionableHandlers = [];
		}
	}

	addMention(mention) {
		this.mentions.push(mention.item);
	}

	removeMention(user) {
		const idx = this.mentions.indexOf(user);
		if (idx > -1) {
			this.mentions.splice(idx, 1);
		}
	}

	//process and sort private comments by user/parent comment
	processPrivateComments() {
		let tempPrivateComments = {};
		this.privateCommentsLength = 0; //reset private comment length
		this.history.forEach(c => {
			if (c.private_comment == 1) {
				//check if logged in user has submitted a private comment if they have stop checking to see if they have
				if (!this.userCanViewPrivateComments) {
					if (c.commenter.id == this.Identity.user.id) {
						//we use this variable to determine whether or not to show the private comment column if the current user isnt the author
						this.userCanViewPrivateComments = true;
					}
				}
				//compile all private comments into their respective "threads" sort them by date
				if (!c.private_comment_parent || c.private_comment_parent == 0) {
					if (!(c.commenter.id in tempPrivateComments)) {
						tempPrivateComments[c.commenter.id] = [];
					}
					tempPrivateComments[c.commenter.id].push(c);
					this.privateCommentsLength++; //for show more toggle on web page
				} else {
					if (!(c.comment_parent_author in tempPrivateComments)) {
						tempPrivateComments[c.comment_parent_author] = [];
					}
					tempPrivateComments[c.comment_parent_author].push(c);
					this.privateCommentsLength++; //for show more toggle on web page
				}
			}
		});

		this.privateCommentsHistory = tempPrivateComments;
	}

	replyToComment(comment) {
		this.replyCommentText = '';
		this.replyMode = true;
		this.toReply = comment;
	}

	cancelReply() {
		this.replyMode = false;
		this.toReply = null;
	}

	saveReplyOnEnter(evt) {
		if (evt.keyCode !== 13 || evt.shiftKey) {
			return;
		} else {
			evt.preventDefault();
			this.saveCommentReply();
		}
	}

	saveCommentReply() {
		// trigger the modal to close
		this.replyMode = false;
		const commentText = String(this.replyCommentText).trim();
		if (commentText === '') {
			// nothing to save
			return;
		}
		let parentCommentId = '';
		let parentCommentAuthor = '';

		if (this.toReply.private_comment_parent && this.toReply.private_comment_parent != '0') {
			console.log('first');
			parentCommentId = this.toReply.private_comment_parent;
			parentCommentAuthor = this.toReply.comment_parent_author;
		} else {
			console.log('second');
			parentCommentId = this.toReply.id;
			parentCommentAuthor = this.toReply.commenter.id;
		}
		console.log('toReply: ', this.toReply);
		console.log(parentCommentId);
		this.Api.create(`/v2/posts/${this.toReply.post_id}/comments`, {
			text: commentText,
			commenter: this.currentUser.id,
			private_comment_parent: parentCommentId,
			private_comment: 1,
		}).then(commentId => {
			if (!commentId) {
				console.error('Did not receive comment id. It may not have saved. Skipping mention alerts.');
				return false;
			}

			const newComment = {
				isNew: true,
				type: 'comment',
				text: commentText,
				date_created: new Date(),
				post_id: this.post.id,
				private_comment: 1,
				private_comment_parent: parentCommentId,
				comment_parent_author: parentCommentAuthor,
				commenter: {
					id: this.currentUser.id,
					email: this.currentUser.email,
					fname: this.currentUser.fname_preferred || this.currentUser.fname,
					lname: this.currentUser.lname_preferred || this.currentUser.lname,
					avatar: this.currentUser.avatar,
				},
				id: commentId,
			};
			this.history.push(newComment);
			this.processPrivateComments();
		});
	}
}
